import {messages} from '@smartmixin/i18n';
import {ArrowLeft, FileText, House} from '@phosphor-icons/react';
import {Box, Breadcrumbs, Button, Link, Typography} from '@mui/material';
import {useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {useLocation, useParams} from 'react-router';
import RouterLink from '../../../lib/RouterLink';
import AppPaper from '../../../components/AppPaper';
import EmptyState from '../../../components/EmptyState';
import ArticleView from '../../../help-center/components/ArticleView';
import HelpEscalation from '../../../help-center/components/HelpEscalation';
import SearchBar from '../../../help-center/components/SearchBar';
import TableOfContents from '../../../help-center/components/TableOfContents';
import {CATEGORY_MESSAGES} from '../../../help-center/constants';
import {useArticles} from '../../../help-center/hooks/useArticles';
import {
  useHelpAlternates,
  useHelpBasePath,
} from '../../../help-center/hooks/useHelpLocale';
import {
  extractHeadings,
  HEADING_SCROLL_OFFSET,
} from '../../../help-center/utils/headings';
import SmartMixinHelmet from '../../../lib/SmartMixinHelmet';
import StructuredData from '../../../lib/StructuredData';

// Articles with fewer h2/h3 sections than this don't get a table of contents
const MIN_TOC_HEADINGS = 2;

const Component = () => {
  const {slug} = useParams<{slug: string}>();
  const {hash} = useLocation();
  const {getArticleBySlug} = useArticles();
  const {formatMessage} = useIntl();
  const helpBasePath = useHelpBasePath();
  const alternates = useHelpAlternates(slug);

  const helpCenterTitle = formatMessage(messages.common.helpCenter);
  const backToHelpCenter = formatMessage(messages.common.backToHelpCenter);
  const article = slug ? getArticleBySlug(slug) : undefined;

  const headings = useMemo(
    () => extractHeadings(article?.content ?? ''),
    [article?.content],
  );
  const showToc = headings.length >= MIN_TOC_HEADINGS;

  // Honor #heading deep links: wait a frame so the article is rendered and
  // ScrollToTop's scroll reset has run, then jump to the section.
  useEffect(() => {
    if (!hash) {
      return undefined;
    }
    const id = decodeURIComponent(hash.slice(1));
    const frame = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView();
    });
    return () => cancelAnimationFrame(frame);
  }, [hash, article?.id]);

  if (!article) {
    return (
      <>
        <SmartMixinHelmet
          title={`${formatMessage(messages.common.articleNotFound)} - ${helpCenterTitle}`}
        />
        <EmptyState
          icon={<FileText />}
          title={formatMessage(messages.common.articleNotFound)}
          description={formatMessage(
            messages.common.articleNotFoundDescription,
          )}
          action={{
            label: backToHelpCenter,
            onClick: () => (window.location.href = helpBasePath),
          }}
          size="large"
        />
      </>
    );
  }

  const categoryLabel = CATEGORY_MESSAGES[article.category]
    ? formatMessage(CATEGORY_MESSAGES[article.category])
    : article.category;

  const articleUrl = `https://smartmixin.io${helpBasePath}/${article.slug}`;
  const categoryUrl = `https://smartmixin.io${helpBasePath}/category/${article.category}`;

  // Extract first 160 chars of content as description (strip markdown)
  const articleExcerpt =
    article.content
      .replace(/[#*_`\[\]()]/g, '')
      .replace(/\n+/g, ' ')
      .trim()
      .slice(0, 160)
      .trim() + '...';

  return (
    <>
      <SmartMixinHelmet
        title={`${article.title} - ${helpCenterTitle}`}
        description={articleExcerpt}
        type="article"
        alternates={alternates}
      />
      <StructuredData
        data={[
          {
            type: 'Article',
            headline: article.title,
            description: articleExcerpt,
            url: articleUrl,
            author: 'SmartMixin',
            inLanguage: article.locale,
          },
          {
            type: 'BreadcrumbList',
            items: [
              {
                name: helpCenterTitle,
                url: `https://smartmixin.io${helpBasePath}`,
              },
              {name: categoryLabel, url: categoryUrl},
              {name: article.title, url: articleUrl},
            ],
          },
        ]}
      />
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{mb: 3}} separator="›">
        <Link
          component={RouterLink}
          to={helpBasePath}
          underline="hover"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            '&:hover': {color: 'primary.main'},
          }}>
          <House size={18} />
          {helpCenterTitle}
        </Link>
        <Link
          component={RouterLink}
          to={`${helpBasePath}/category/${article.category}`}
          underline="hover"
          sx={{
            color: 'text.secondary',
            '&:hover': {color: 'primary.main'},
          }}>
          {categoryLabel}
        </Link>
        <Typography
          sx={{
            color: 'text.primary',
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {article.title}
        </Typography>
      </Breadcrumbs>
      {/* Search bar */}
      <SearchBar />
      {/* Article content with table of contents */}
      <Box sx={{display: 'flex', gap: 4, alignItems: 'flex-start'}}>
        <Box sx={{flexGrow: 1, minWidth: 0}}>
          <AppPaper sx={{p: {xs: 3, md: 4}}}>
            {showToc && (
              <Box sx={{display: {xs: 'block', lg: 'none'}}}>
                <TableOfContents headings={headings} variant="inline" />
              </Box>
            )}
            <ArticleView article={article} />
          </AppPaper>
          {/* Escalation: self-serve failed, make contact easy */}
          <HelpEscalation articleTitle={article.title} />
        </Box>
        {showToc && (
          <Box
            component="aside"
            sx={{
              width: 240,
              flexShrink: 0,
              display: {xs: 'none', lg: 'block'},
              position: 'sticky',
              top: `${HEADING_SCROLL_OFFSET}px`,
              maxHeight: `calc(100vh - ${HEADING_SCROLL_OFFSET + 24}px)`,
              overflowY: 'auto',
              pt: 1,
            }}>
            <TableOfContents headings={headings} variant="rail" />
          </Box>
        )}
      </Box>
      {/* Back button */}
      <Box sx={{mt: 4, textAlign: 'center'}}>
        <Button
          component={RouterLink}
          to={helpBasePath}
          startIcon={<ArrowLeft />}
          variant="outlined"
          sx={{borderColor: 'divider'}}>
          {backToHelpCenter}
        </Button>
      </Box>
    </>
  );
};

Component.displayName = 'ArticleViewScreen';

export {Component};
